/**
 * cart-tag-limit.js
 *
 * Enforces "at most one item tagged 'Pack' per cart" on the storefront
 * (quantity counts). Liquid knows product tags (/cart.js does not), so the
 * total tagged quantity is computed server-side in cart-line-items.liquid and
 * rendered as a hidden [data-cart-limit] marker. This script reads that marker
 * and enforces three things:
 *
 *   1. Disables the add-to-cart button on ANY tagged product once the cart
 *      already holds a tagged item (the main behavior).
 *   2. Click guard — also blocks an add that would push the tagged total over
 *      the cap (e.g. adding qty 2 of the first tagged item in one click).
 *   3. Checkout guard — disables the cart "Checkout" button and shows a
 *      message if the cart's tagged total ever exceeds the cap.
 *
 * The tag string lives only in the Liquid. Inspect live state in the console
 * via `window.cartTagLimit`.
 *
 * NOTE: This is the friendly storefront layer and is bypassable. The
 * unbypassable backstop is the Cart Validation Function in the spend-cap app.
 */
(function () {
  'use strict';

  var ADD_MESSAGE =
    'You already have one of these in your cart. Only one can be purchased per order.';
  var CHECKOUT_MESSAGE =
    'Only one of these items can be purchased per order. Please reduce the quantity to one to continue.';
  var MSG_STYLE =
    'margin:10px 0;padding:10px 12px;border-radius:6px;background:#fdecea;color:#611a15;font-size:14px;line-height:1.4;';

  var currentLimitedQty = 0;
  var limitMax = 1;

  // Expose state for debugging in the browser console.
  window.cartTagLimit = {
    get currentLimitedQty() { return currentLimitedQty; },
    get limitMax() { return limitMax; },
    refresh: function () { return refreshAndApply(); },
  };

  // Fetch the cart-items section fresh and read the marker. Authoritative and
  // independent of the theme's own re-render timing.
  function refreshFromSection() {
    var root =
      (window.theme && window.theme.routes && window.theme.routes.root_url) || '';
    return fetch(root + '?section_id=api-cart-items')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var div = document.createElement('div');
        div.innerHTML = html;
        readMarkerFrom(div);
      })
      .catch(function () {});
  }

  function readMarkerFrom(scope) {
    var marker = scope.querySelector('[data-cart-limit]');
    if (!marker) return false;
    currentLimitedQty = parseInt(marker.getAttribute('data-limited-qty'), 10) || 0;
    limitMax = parseInt(marker.getAttribute('data-limit-max'), 10) || 1;
    return true;
  }

  function buildMessage(text) {
    var el = document.createElement('div');
    el.setAttribute('data-cart-limit-message', '');
    el.setAttribute('role', 'alert');
    el.textContent = text;
    el.style.cssText = MSG_STYLE;
    return el;
  }

  // --- Apply state to the DOM -------------------------------------------------

  function applyState() {
    // At/over the cap -> no more tagged items may be added.
    var atCap = currentLimitedQty >= limitMax;
    // Strictly over the cap -> the cart itself is invalid, block checkout.
    var overCap = currentLimitedQty > limitMax;

    // 1. Disable add-to-cart buttons on tagged products.
    var addButtons = document.querySelectorAll(
      '[data-add-to-cart][data-cart-limited="true"]'
    );
    for (var i = 0; i < addButtons.length; i++) {
      var btn = addButtons[i];
      btn.disabled = atCap;
      btn.classList.toggle('cart-limit-disabled', atCap);
      btn.setAttribute('aria-disabled', atCap ? 'true' : 'false');
      toggleNoteNear(btn, atCap, ADD_MESSAGE);
    }

    // 3. Block checkout if the cart is already over the cap.
    var checkoutButtons = document.querySelectorAll('[name="checkout"]');
    for (var j = 0; j < checkoutButtons.length; j++) {
      var cbtn = checkoutButtons[j];
      cbtn.disabled = overCap;
      cbtn.classList.toggle('cart-limit-blocked', overCap);
      toggleNoteNear(cbtn, overCap, CHECKOUT_MESSAGE);
    }
  }

  // Insert/remove a message immediately before the given button.
  function toggleNoteNear(btn, show, text) {
    var holder = btn.parentNode;
    if (!holder) return;
    var existing = holder.querySelector(':scope > [data-cart-limit-message]');
    if (show && !existing) {
      holder.insertBefore(buildMessage(text), btn);
    } else if (!show && existing) {
      existing.remove();
    }
  }

  // --- Click guard (catches qty>1 on the first tagged add) --------------------

  function onAddClick(e) {
    var target = e.target;
    var btn = target.closest && target.closest('[data-add-to-cart]');
    if (!btn || btn.getAttribute('data-cart-limited') !== 'true') return;

    var form = btn.closest('[data-product-form]');
    var qtyInput = form && form.querySelector('[name="quantity"]');
    var adding = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    if (currentLimitedQty + adding > limitMax) {
      // Capture phase: stop the theme's own (bubbling) add-to-cart handler.
      e.preventDefault();
      e.stopImmediatePropagation();
      var holder = (form && form.querySelector('[data-error-display]')) || btn.parentNode;
      if (holder && !holder.querySelector('[data-cart-limit-message]')) {
        var msg = buildMessage(ADD_MESSAGE);
        holder.appendChild(msg);
        setTimeout(function () { if (msg.parentNode) msg.parentNode.removeChild(msg); }, 6000);
      }
    }
  }

  // --- Wiring -----------------------------------------------------------------

  // Capture phase so we run before the theme's add-to-cart handler.
  document.addEventListener('click', onAddClick, true);

  function refreshAndApply() {
    return refreshFromSection().then(applyState);
  }

  document.addEventListener('theme:cart:change', refreshAndApply);
  document.addEventListener('theme:cart:reload', refreshAndApply);

  if (document.readyState !== 'loading') {
    refreshAndApply();
  } else {
    document.addEventListener('DOMContentLoaded', refreshAndApply);
  }
})();
